import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";

import { ChoiceChips } from "../design/ChoiceChips";
import { AdvisorInvoiceNotice } from "../operations/PayableRequirementHint";
import { formatMxn } from "../quoter/PlanSchedule";
import type { AppRole } from "../types";
import type { PayableStatus, PayableSummary } from "./invoicingTypes";
import {
  PAYABLE_FILTER_STATUSES,
  isOwnPayablesRole,
} from "./payableScreenRules";

const StatusChips = () => {
  const translate = useTranslate();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const status = filterValues.status as PayableStatus | undefined;
  return (
    <ChoiceChips
      label={translate("crm.invoicing.payables.status_filter")}
      mode="single"
      value={status ? [status] : []}
      options={PAYABLE_FILTER_STATUSES.map((id) => ({
        id,
        label: translate(`crm.invoicing.status.payable.${id}`),
      }))}
      onChange={([next]) => {
        const rest = { ...filterValues };
        delete rest.status;
        setFilters(next ? { ...rest, status: next } : rest, displayedFilters);
      }}
    />
  );
};

const PayableListBody = ({ role }: { role: AppRole }) => {
  const translate = useTranslate();
  const own = isOwnPayablesRole(role);
  // Mis pagos: no ordering, so nobody can rank or compare advisors.
  const disableSort = own;
  return (
    <div className="space-y-4">
      {own ? <AdvisorInvoiceNotice role={role} /> : null}
      <StatusChips />
      <DataTable rowClick="show" bulkActionButtons={false}>
        <DataTable.Col
          source="operation_id"
          label="crm.invoicing.fields.operation"
          disableSort={disableSort}
        />
        {own ? null : (
          <DataTable.Col
            source="payee_name"
            label="crm.invoicing.fields.payee"
            render={(record: PayableSummary) =>
              `${record.payee_name ?? "—"} (${translate(
                `crm.invoicing.payee_role.${record.payee_role}`,
              )})`
            }
          />
        )}
        <DataTable.Col
          source="project_code"
          label="crm.invoicing.fields.project_or_address"
          disableSort={disableSort}
          render={(record: PayableSummary) =>
            record.project_code ?? record.property_address ?? "—"
          }
        />
        <DataTable.Col
          source="unit_identifier"
          label="crm.invoicing.fields.unit"
          disableSort={disableSort}
          render={(record: PayableSummary) => record.unit_identifier ?? "—"}
        />
        <DataTable.Col
          source="status"
          label="crm.invoicing.fields.status"
          disableSort={disableSort}
          render={(record: PayableSummary) =>
            translate(`crm.invoicing.status.payable.${record.status}`)
          }
        />
        <DataTable.Col
          source="total_amount"
          label="crm.invoicing.fields.total"
          disableSort={disableSort}
          className="text-right tabular-nums"
          render={(record: PayableSummary) => formatMxn(record.total_amount)}
        />
      </DataTable>
    </div>
  );
};

/**
 * Almikia Epic E6 (TASK-041): "Cuentas por pagar" over `payables_summary`.
 * direccion / contabilidad / auditoria read every payable (the actions live on
 * the detail page); an asesora / lider reads ONLY her own rows, titled "Mis
 * pagos", with no totals and no sorting. Newest first, server-side pagination,
 * search by operation id, status chips.
 */
export const PayableList = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  if (!identity) return null;
  const own = isOwnPayablesRole(identity.role as AppRole);
  return (
    <List
      resource="payables"
      title={own ? translate("crm.invoicing.payables.my_title") : undefined}
      sort={{ field: "created_at", order: "DESC" }}
      perPage={25}
      filters={[
        <SearchInput
          key="q"
          source="q"
          alwaysOn
          placeholder={translate("crm.invoicing.payables.search")}
        />,
      ]}
      actions={false}
    >
      <PayableListBody role={identity.role as AppRole} />
    </List>
  );
};
